/**
 * EVO — hisobot ekranlarining URL holati.
 *
 * `list-search.ts` bilan bir xil sabab: «shu oy, mijoz bo'yicha» ekranini
 * rahbar moliyachiga havola qilib yuboradi va u AYNAN o'shani ochadi.
 * Hisobotda bu ro'yxatdagidan ham muhimroq — hisobot haqidagi suhbat deyarli
 * har doim «qaysi davr uchun?» degan savoldan boshlanadi.
 *
 * URL ISHONCHSIZ KIRISH: har maydon tozalanadi. `?from=DROP TABLE` yoki
 * `?days=-1` ekranni buzmaydi, default'ga tushadi. Server ham o'z tekshiruvini
 * qiladi (`ZodQueryPipe`) — bu ikkinchi qatlam, birinchisi emas.
 *
 * DEFAULT QIYMATLAR URL GA YOZILMAYDI (`toListSearchParams` bilan bir xil
 * yondashuv): `?groupBy=day&sortDir=asc` ko'rinishidagi uzun manzil
 * «bu yerda nimadir sozlangan» degan noto'g'ri taassurot beradi.
 */
import { compareDecimals, sumDecimals } from './decimal';
import { defaultPeriod, isIsoDate, normalizePeriod, type Period } from './period';
import { PROFIT_BY, SALES_GROUP_BY, type ProfitBy, type SalesGroupBy } from './reports';

// ---------------------------------------------------------------------------
// Davr (`from`/`to`)
// ---------------------------------------------------------------------------

export interface PeriodSearch {
  readonly from?: string;
  readonly to?: string;
}

/**
 * URL dagi davrni to'liq davrga aylantiradi.
 *
 * IKKALASI ham bo'lishi shart: faqat `from` berilgan bo'lsa «qaysi kungacha?»
 * degan savol javobsiz qoladi va API 400 qaytaradi. Yarim to'ldirilgan davr —
 * default davr (shu oy).
 */
export function resolvePeriod(search: PeriodSearch): Period {
  if (isIsoDate(search.from) && isIsoDate(search.to)) {
    return normalizePeriod({ from: search.from, to: search.to });
  }
  return defaultPeriod();
}

export function parsePeriodSearch(search: Record<string, unknown>): PeriodSearch {
  return {
    ...(isIsoDate(search.from) ? { from: search.from } : {}),
    ...(isIsoDate(search.to) ? { to: search.to } : {}),
  };
}

/** Davrni URL parametrlariga qaytaradi (navigatsiya uchun). */
export function toPeriodSearch(period: Period): PeriodSearch {
  return { from: period.from, to: period.to };
}

// ---------------------------------------------------------------------------
// Savdo hisoboti — davr + kesim
// ---------------------------------------------------------------------------

export interface SalesReportSearch extends PeriodSearch {
  readonly groupBy?: SalesGroupBy;
}

function isSalesGroupBy(value: unknown): value is SalesGroupBy {
  return typeof value === 'string' && (SALES_GROUP_BY as readonly string[]).includes(value);
}

export function parseSalesReportSearch(search: Record<string, unknown>): SalesReportSearch {
  return {
    ...parsePeriodSearch(search),
    // `day` — default, shuning uchun URL ga yozilmaydi.
    ...(isSalesGroupBy(search.groupBy) && search.groupBy !== 'day' ? { groupBy: search.groupBy } : {}),
  };
}

// ---------------------------------------------------------------------------
// Yalpi foyda — davr + kesim
// ---------------------------------------------------------------------------

export interface ProfitReportSearch extends PeriodSearch {
  readonly by?: ProfitBy;
}

function isProfitBy(value: unknown): value is ProfitBy {
  return typeof value === 'string' && (PROFIT_BY as readonly string[]).includes(value);
}

export function parseProfitReportSearch(search: Record<string, unknown>): ProfitReportSearch {
  return {
    ...parsePeriodSearch(search),
    ...(isProfitBy(search.by) && search.by !== 'product' ? { by: search.by } : {}),
  };
}

// ---------------------------------------------------------------------------
// AR aging — bitta sana (`asOf`)
// ---------------------------------------------------------------------------

/**
 * Mijoz intizomi — davr + MAJBURIY mijoz.
 *
 * `customerId` manzilda yashaydi va bu ataylab: moliyachi «shu dilerning
 * yili» degan havolani rahbarga yuboradi va u AYNAN o'shani ochadi
 * (`list-search.ts` dagi bir xil sabab). Mijoz tanlanmaguncha hisobot
 * so'ralmaydi — server ham `customerId` siz 400 qaytaradi.
 */
export interface DisciplineReportSearch extends PeriodSearch {
  readonly customerId?: number;
  /** `week` — oy ichidagi to'rtta bo'lak; berilmasa oylik. */
  readonly bucket?: 'week';
}

export function parseDisciplineReportSearch(
  search: Record<string, unknown>,
): DisciplineReportSearch {
  const raw = Number(search['customerId']);
  const customerId = Number.isInteger(raw) && raw > 0 ? raw : undefined;

  return {
    ...parsePeriodSearch(search),
    ...(customerId !== undefined ? { customerId } : {}),
    /* Faqat `week` manzilga yoziladi: oylik — standart va u qisqaroq. */
    ...(search['bucket'] === 'week' ? { bucket: 'week' as const } : {}),
  };
}

export interface AgingReportSearch {
  readonly asOf?: string;
}

export function parseAgingReportSearch(search: Record<string, unknown>): AgingReportSearch {
  return isIsoDate(search.asOf) ? { asOf: search.asOf } : {};
}

// ---------------------------------------------------------------------------
// Dead stock — chegara (kun)
// ---------------------------------------------------------------------------

/** Server qabul qiladigan oraliq: 1…3650 (`DeadStockSchema`). */
export const DEAD_STOCK_DEFAULT_DAYS = 90;
export const DEAD_STOCK_PRESETS = [30, 60, 90, 180, 365] as const;

export interface DeadStockReportSearch {
  readonly days?: number;
}

export function parseDeadStockReportSearch(search: Record<string, unknown>): DeadStockReportSearch {
  const days = Number(search.days);
  // `Number.isInteger` `1.5` va `NaN` ikkalasini ham rad etadi.
  if (!Number.isInteger(days) || days < 1 || days > 3650 || days === DEAD_STOCK_DEFAULT_DAYS) {
    return {};
  }
  return { days };
}

export function resolveDeadStockDays(search: DeadStockReportSearch): number {
  return search.days ?? DEAD_STOCK_DEFAULT_DAYS;
}

// ---------------------------------------------------------------------------
// Konteyner rentabelligi — ixtiyoriy bitta yuk
// ---------------------------------------------------------------------------

export interface ShipmentReportSearch {
  readonly shipmentId?: number;
}

export function parseShipmentReportSearch(search: Record<string, unknown>): ShipmentReportSearch {
  const id = Number(search.shipmentId);
  return Number.isInteger(id) && id > 0 ? { shipmentId: id } : {};
}

// ---------------------------------------------------------------------------
// ABC — davr + SOZLANADIGAN chegara
// ---------------------------------------------------------------------------

/**
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ CHEGARA URL DA — CHUNKI U NATIJANI O'ZGARTIRADI                          ║
 * ║                                                                          ║
 * ║ `reorder` parametrlari ataylab URL da EMAS: ular hisoblash FARAZI va     ║
 * ║ xulosa («shu SKU larni buyurtma qiling») ular bilan ham, ularsiz ham     ║
 * ║ bir xil chiqadi.                                                         ║
 * ║                                                                          ║
 * ║ ABC da esa teskari: chegara TASNIFNI o'zgartiradi. «Bu tovar C guruhda»  ║
 * ║ degan xulosa 80/15/5 da bir xil, 60/25/15 da boshqacha. Ya'ni ekran      ║
 * ║ havola qilib yuborilganda chegara ham u bilan borishi SHART — aks holda  ║
 * ║ ikki odam bir havolada boshqa-boshqa guruhni ko'radi.                    ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 *
 * Chegara `string` bo'lib qoladi: u serverda decimal arifmetikaga tushadi va
 * `number` ga aylantirish float xatosini kiritardi (`lib/decimal.ts` qoidasi).
 * Tekshiruv shakl bo'yicha — regex, `parseFloat` emas.
 */
const PCT_SHAPE = /^\d{1,3}(\.\d{1,4})?$/;

/** `"80"` va `"80.0"` bir xil chegara — solishtirish uchun normallashtiriladi. */
function isPct(value: unknown): value is string {
  return typeof value === 'string' && PCT_SHAPE.test(value);
}

export const ABC_DEFAULT_A_PCT = '80';
export const ABC_DEFAULT_B_PCT = '15';
/**
 * Tayyor kesimlar: klassik Pareto, yumshoqroq va qattiqroq variant.
 *
 * `cPct` YOZIB QO'YILGAN, hisoblanmagan: u shunchaki yorliq va uni ekranda
 * `100 − a − b` deb hisoblash `Number()` ni bu qatlamga olib kirardi. Pul va
 * nisbat bu yerda `string` bo'lib qoladi (`lib/decimal.ts` qoidasi), server
 * esa javobda o'z `cPct` ini decimal bilan hisoblab qaytaradi — ya'ni
 * jadvaldagi haqiqiy chegara har doim serverdan keladi.
 */
export const ABC_PRESETS = [
  { aPct: '80', bPct: '15', cPct: '5' },
  { aPct: '70', bPct: '20', cPct: '10' },
  { aPct: '50', bPct: '30', cPct: '20' },
] as const;

export interface AbcReportSearch extends PeriodSearch {
  readonly aPct?: string;
  readonly bPct?: string;
}

export function parseAbcReportSearch(search: Record<string, unknown>): AbcReportSearch {
  const aPct = isPct(search.aPct) ? search.aPct : undefined;
  const bPct = isPct(search.bPct) ? search.bPct : undefined;

  /*
   * Ikkalasi HAM to'g'ri bo'lishi va yig'indisi 100 dan oshmasligi kerak.
   * Yarim to'ldirilgan yoki qarama-qarshi chegara — standartga tushadi:
   * server 400 qaytarardi va ekran «hisobot buzuq» bo'lib ko'rinardi
   * (`resolvePeriod()` dagi bir xil mulohaza).
   *
   * Yig'indi `sumDecimals`/`compareDecimals` bilan tekshiriladi —
   * `Number()` bilan EMAS. Chegara serverda decimal arifmetikaga tushadi va
   * bu yerda float bilan tekshirish ikki qatlamni AJRATIB YUBORARDI:
   * brauzer «yaroqli» degan qiymatni server rad etishi mumkin edi
   * (`lib/decimal.ts` fayl boshidagi qoida).
   */
  if (aPct === undefined || bPct === undefined) return parsePeriodSearch(search);
  const sum = sumDecimals([aPct, bPct]);
  if (sum === null || compareDecimals(sum, '100') > 0) return parsePeriodSearch(search);
  if (aPct === ABC_DEFAULT_A_PCT && bPct === ABC_DEFAULT_B_PCT) return parsePeriodSearch(search);

  return { ...parsePeriodSearch(search), aPct, bPct };
}

export function resolveAbcThresholds(search: AbcReportSearch): { aPct: string; bPct: string } {
  return {
    aPct: search.aPct ?? ABC_DEFAULT_A_PCT,
    bPct: search.bPct ?? ABC_DEFAULT_B_PCT,
  };
}

// ---------------------------------------------------------------------------
// XYZ — kuzatuv oynasi va davr turi
// ---------------------------------------------------------------------------

export const XYZ_DEFAULT_LOOKBACK_DAYS = 180;
export const XYZ_LOOKBACK_PRESETS = [90, 180, 365] as const;

export interface XyzReportSearch {
  readonly lookbackDays?: number;
  readonly bucket?: 'week' | 'month';
}

export function parseXyzReportSearch(search: Record<string, unknown>): XyzReportSearch {
  const days = Number(search.lookbackDays);
  const bucket = search.bucket === 'month' ? 'month' : undefined;
  const validDays =
    Number.isInteger(days) && days >= 28 && days <= 1095 && days !== XYZ_DEFAULT_LOOKBACK_DAYS
      ? days
      : undefined;

  return {
    ...(validDays !== undefined ? { lookbackDays: validDays } : {}),
    ...(bucket ? { bucket } : {}),
  };
}

// ---------------------------------------------------------------------------
// Ombor yoshi — ixtiyoriy ombor
// ---------------------------------------------------------------------------

export interface StockAgeReportSearch {
  readonly warehouseId?: number;
}

export function parseStockAgeReportSearch(search: Record<string, unknown>): StockAgeReportSearch {
  const id = Number(search.warehouseId);
  return Number.isInteger(id) && id > 0 ? { warehouseId: id } : {};
}

// ---------------------------------------------------------------------------
// Kuzatuv oynasi bilan ishlaydigan hisobotlar (ta'minotchi, konteyner)
// ---------------------------------------------------------------------------

export const SUPPLIER_LOOKBACK_DEFAULT = 365;
export const SUPPLIER_LOOKBACK_PRESETS = [90, 180, 365, 730] as const;
export const CONTAINER_LOOKBACK_DEFAULT = 730;
export const CONTAINER_LOOKBACK_PRESETS = [180, 365, 730, 1095] as const;

export interface LookbackReportSearch {
  readonly lookbackDays?: number;
}

/**
 * Kuzatuv oynasini o'qiydi.
 *
 * `defaultDays` PARAMETR: ikki hisobotda standart boshqacha (ta'minotchi 365,
 * konteyner 730) va standart qiymat URL ga YOZILMAYDI. Bitta umumiy konstanta
 * qo'yilsa, ikkinchi hisobotning URL i har doim parametr bilan chiqardi.
 */
export function parseLookbackSearch(
  search: Record<string, unknown>,
  defaultDays: number,
): LookbackReportSearch {
  const days = Number(search.lookbackDays);
  if (!Number.isInteger(days) || days < 30 || days > 3650 || days === defaultDays) return {};
  return { lookbackDays: days };
}

export function parseSupplierScorecardSearch(
  search: Record<string, unknown>,
): LookbackReportSearch {
  return parseLookbackSearch(search, SUPPLIER_LOOKBACK_DEFAULT);
}

export function parseContainerReportSearch(search: Record<string, unknown>): LookbackReportSearch {
  return parseLookbackSearch(search, CONTAINER_LOOKBACK_DEFAULT);
}

// ---------------------------------------------------------------------------
// LTV — eng qisqa tarix chegarasi
// ---------------------------------------------------------------------------

export const LTV_DEFAULT_MIN_LIFESPAN = 30;
export const LTV_LIFESPAN_PRESETS = [7, 30, 90] as const;

export interface LtvReportSearch {
  readonly minLifespanDays?: number;
}

export function parseLtvReportSearch(search: Record<string, unknown>): LtvReportSearch {
  const days = Number(search.minLifespanDays);
  if (!Number.isInteger(days) || days < 1 || days > 3650 || days === LTV_DEFAULT_MIN_LIFESPAN) {
    return {};
  }
  return { minLifespanDays: days };
}
